/**
 * practiceMetrics.js — pure firm-level rollups + trend series for the Practice Performance dashboard
 * (CT-025). Turns the measured snapshot (one date) into firm composite scores + distributions, and a
 * span of snapshot history into chartable time series. PURE (no DOM, no imports) so it is node-testable.
 *
 * The snapshot rows are the two sensors made durable: hours (effort) and completions (output), plus
 * the as-of-now plan signals (overdue / at-risk). Firm scores condense them the way Bevel condenses a
 * day of tracker data into a few legible numbers.
 */

import { bestCandidate } from './eligibility.js'

function n(v) { const x = Number(v); return Number.isFinite(x) ? x : 0 }
function avg(xs) { return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null }
function round(x, p = 0) { const m = Math.pow(10, p); return x == null ? null : Math.round(x * m) / m }

const HOT = 1.3, LIGHT = 0.8

/** Flatten the scheduler_config rows into a { key: number } map for the lever-aware functions. */
export function leverMap(levers) {
  const m = {}
  ;(levers || []).forEach(l => { const v = Number(l.value); if (l && l.key && Number.isFinite(v)) m[l.key] = v })
  return m
}

/**
 * Firm composite from one snapshot's member + project rows.
 * @param {Array} members  workload_snapshots rows for a single date
 * @param {Array} projects project_snapshots rows for the same date
 */
export function rollupFirm(members, projects, lv = {}) {
  const hot = lv.load_hot ?? HOT, light = lv.load_light ?? LIGHT
  const ms = members || [], ps = projects || []
  const loads = ms.map(m => m.load_ratio).filter(v => v != null).map(Number)
  const withHours = ms.filter(m => n(m.hours_7d) > 0)

  const loadByPerson = ms
    .filter(m => m.load_ratio != null || n(m.hours_7d) > 0)
    .map(m => ({ name: cleanStr(m.member_name) || '—', value: m.load_ratio != null ? Number(m.load_ratio) : null, hours7d: round(n(m.hours_7d), 1) }))
    .sort((a, b) => (b.value ?? -1) - (a.value ?? -1))

  const throughputByPerson = ms
    .map(m => ({ name: cleanStr(m.member_name) || '—', value: n(m.completions_7d) }))
    .filter(p => p.value > 0)
    .sort((a, b) => b.value - a.value)

  // Defensive: clean any bracketed lookup-string names that predate the snapshot name fix, so the UI
  // reads cleanly without waiting for a re-capture.
  const projectPressure = ps
    .map(p => ({ name: cleanStr(p.project_name) || '—', value: round(n(p.pressure), 1), overdue: n(p.overdue), open: n(p.open_milestones) }))
    .filter(p => p.value > 0 || p.overdue > 0)
    .sort((a, b) => b.value - a.value)

  return {
    people: ms.length,
    activePeople: withHours.length,
    avgLoad: round(avg(loads), 2),
    totalHours7d: round(ms.reduce((s, m) => s + n(m.hours_7d), 0), 1),
    totalHours28d: round(ms.reduce((s, m) => s + n(m.hours_28d), 0), 1),
    throughput7d: ms.reduce((s, m) => s + n(m.completions_7d), 0),
    throughput28d: ms.reduce((s, m) => s + n(m.completions_28d), 0),
    overdue: ms.reduce((s, m) => s + n(m.overdue), 0),
    atRisk: ms.reduce((s, m) => s + n(m.at_risk), 0),
    overloaded: loads.filter(v => v > hot).length,
    light: loads.filter(v => v < light).length,
    loadByPerson,
    throughputByPerson,
    projectPressure,
  }
}

/** Group history rows by snapshot_date and roll each date up to firm-level series points. */
export function buildTrends({ members = [], projects = [], runs = [] } = {}) {
  const byDate = new Map()
  const ensure = d => { if (!byDate.has(d)) byDate.set(d, { date: d, hours7d: 0, throughput7d: 0, overdue: 0, atRisk: 0, _loads: [] }); return byDate.get(d) }
  members.forEach(m => {
    const e = ensure(m.snapshot_date)
    e.hours7d += n(m.hours_7d)
    e.throughput7d += n(m.completions_7d)
    e.overdue += n(m.overdue)
    e.atRisk += n(m.at_risk)
    if (m.load_ratio != null) e._loads.push(Number(m.load_ratio))
  })
  const dates = [...byDate.keys()].sort()
  const point = (d, key) => { const e = byDate.get(d); return { x: d, y: key === 'avgLoad' ? round(avg(e._loads), 2) : round(e[key], 1) } }

  const runByDate = new Map()
  runs.forEach(r => runByDate.set(r.run_date, r))
  const runDates = [...runByDate.keys()].sort()

  return {
    dates,
    hours:      dates.map(d => point(d, 'hours7d')),
    avgLoad:    dates.map(d => point(d, 'avgLoad')).filter(p => p.y != null),
    throughput: dates.map(d => point(d, 'throughput7d')),
    atRisk:     dates.map(d => point(d, 'atRisk')),
    overdue:    dates.map(d => point(d, 'overdue')),
    movement:   runDates.map(d => { const r = runByDate.get(d); return { x: d, pushed: n(r.pushed), pulled: n(r.pulled) } }),
  }
}

/* ── Demand vs Capacity (the leveling check + hiring signal) ──────────────────────────────────────
 * Over-commitment is NOT a milestone count. It is the scheduler's own measured signal: how many
 * budgeted person-days cannot fit before their deadlines at current capacity ("Days Budgeted
 * Impossible"). A person can be Responsible for 100 future milestones and be perfectly steady — that
 * backlog is DEMAND, sequenced over time, not overload. We read the scheduler's verdict, never
 * re-derive a worse one from raw concurrency. */

const DAYS_OVER = 5    // more than ~a week of work that can't fit on time → over-committed
const DAYS_TIGHT = 0   // any infeasible days → tight (worth watching, not an emergency)

function fOf(c, k) { const v = (c.fields || {})[k]; return Array.isArray(v) ? (v[0] ?? null) : (v ?? null) }
function nOf(c, k) { const x = Number(fOf(c, k)); return Number.isFinite(x) ? x : 0 }
function cName(c) {
  const f = c.fields || {}
  return [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || f['Name'] || '—'
}
export function capacityStatus(infeasible, lv = {}) {
  const over = lv.capacity_over_days ?? DAYS_OVER
  const tight = lv.capacity_tight_days ?? DAYS_TIGHT
  return infeasible > over ? 'over' : infeasible > tight ? 'tight' : 'steady'
}

/**
 * Firm demand-vs-capacity from the Team contacts (the scheduler's maintained capacity fields).
 * @param {Array} contacts raw Team contact rows
 * @returns { committedDays, infeasibleDays, tight, over, people:[{name, committed, infeasible, maxConcurrent, status}] }
 */
export function rollupCapacity(contacts, lv = {}) {
  const people = (contacts || []).map(c => {
    const committed = round(nOf(c, 'Total Days Budgeted'), 1)
    const infeasible = round(nOf(c, 'Days Budgeted Impossible'), 1)
    const maxConcurrent = fOf(c, 'Max Concurrent Milestones')
    return { name: cName(c), committed, infeasible, maxConcurrent: maxConcurrent == null ? null : Number(maxConcurrent), status: capacityStatus(infeasible, lv) }
  }).filter(p => p.committed > 0 || p.infeasible > 0)
    .sort((a, b) => b.infeasible - a.infeasible || b.committed - a.committed)

  return {
    committedDays: round(people.reduce((s, p) => s + p.committed, 0), 1),
    infeasibleDays: round(people.reduce((s, p) => s + p.infeasible, 0), 1),
    tight: people.filter(p => p.status === 'tight').length,
    over: people.filter(p => p.status === 'over').length,
    people,
  }
}

/* ── Efficiency / constraint finder (CT-025 Build 2) ──────────────────────────────────────────────
 * Where the firm loses time: phases running over budget (spending faster than they progress) and work
 * that's stalled (open milestones, no recent time entries). Read from the Phases table's own computed
 * budget-vs-actual + activity fields — not re-derived. */

const STALL_DAYS = 14

function cleanStr(v) {
  let s = fOfv(v)
  if (s == null) return ''
  s = String(s).trim()
  if (s.startsWith('[')) { try { const a = JSON.parse(s); if (Array.isArray(a) && a.length) return String(a[0]) } catch (e) { /* */ } return s.replace(/^\[|\]$|"/g, '') }
  return s
}
function fOfv(v) { return Array.isArray(v) ? (v[0] ?? null) : (v ?? null) }
function lenNum(v) { if (Array.isArray(v)) return v.length; const x = Number(v); return Number.isFinite(x) ? x : 0 }
function daysAgo(ymd, today) { const a = Date.parse(ymd + 'T00:00:00Z'), b = Date.parse(today + 'T00:00:00Z'); return (Number.isNaN(a) || Number.isNaN(b)) ? null : Math.floor((b - a) / 86400000) }

/**
 * @param {Array} phases   raw active-phase rows (getPhaseEfficiency)
 * @param {string} todayYmd
 * @returns { overBudget:[...], stalled:[...], phases:number }
 */
export function rollupEfficiency(phases, todayYmd) {
  const items = (phases || []).map(p => {
    const f = p.fields || {}
    const budget = round(Number(fOf(p, 'Hours Budget')) || 0, 1)
    const expended = round(Number(fOf(p, 'Hours Expended')) || 0, 1)
    let pc = Number(fOf(p, 'Percent Complete')); if (!Number.isFinite(pc)) pc = 0; if (pc > 1.5) pc = pc / 100
    const feePerf = Number(fOf(p, 'Fee Performance Percent'))
    const recent = fOf(p, 'Most Recent Time Entry')
    const recentYmd = recent ? String(recent).slice(0, 10) : null
    return {
      project: cleanStr(f['Project Name']) || '—',
      phase: cleanStr(f['Phase Name']) || '—',
      budget, expended,
      pctUsed: budget > 0 ? expended / budget : 0,
      pctComplete: pc,
      feePerf: Number.isFinite(feePerf) ? feePerf : null,
      openMs: lenNum(f['Open Milestones']),
      idle: recentYmd ? daysAgo(recentYmd, todayYmd) : null,
    }
  })

  // Over budget = spent more hours than budgeted, OR burning meaningfully ahead of progress.
  const overBudget = items
    .filter(r => r.budget > 0 && (r.expended > r.budget || (r.pctUsed - r.pctComplete > 0.2 && r.pctUsed > 0.5)))
    .map(r => ({ ...r, overageHrs: round(r.expended - r.budget, 1), aheadPts: Math.round((r.pctUsed - r.pctComplete) * 100) }))
    .sort((a, b) => (b.pctUsed - b.pctComplete) - (a.pctUsed - a.pctComplete))

  // Stalled = open milestones but no time logged for a while (or never).
  const stalled = items
    .filter(r => r.openMs > 0 && (r.idle == null || r.idle >= STALL_DAYS))
    .sort((a, b) => (b.idle ?? 99999) - (a.idle ?? 99999))

  return { overBudget, stalled, phases: items.length }
}

/* ── Hiring model (CT-025 Build 3) ────────────────────────────────────────────────────────────────
 * Turns the capacity gap into a staffing decision. The gap is the firm's infeasible person-days
 * (committed work that can't be delivered on time at current staffing). A what-if adds capacity and
 * shows how fast it clears the gap. Deliberately simple + honest — it assumes demand holds steady and
 * the new capacity goes to the backlog; it does NOT model onboarding ramp or new incoming work. */

const HIRE_WATCH = 5    // below this, the gap is small — likely resequencing, not hiring
const HIRE_SIGNAL = 20  // sustained gap above this genuinely points to added capacity

/**
 * @param {object} capacity rollupCapacity() result
 * @param {object} opts      { hires:number, daysPerWeek:number }  (daysPerWeek ≈ weekly hours ÷ 8)
 */
export function modelHiring(capacity, { hires = 1, daysPerWeek = 5 } = {}) {
  const gapDays = capacity ? capacity.infeasibleDays : 0
  const committedDays = capacity ? capacity.committedDays : 0
  const overCommitted = capacity ? capacity.people.filter(p => p.infeasible > 0) : []
  const addedPerWeek = round(hires * daysPerWeek, 1)
  const weeksToClear = (gapDays > 0 && addedPerWeek > 0) ? Math.ceil(gapDays / addedPerWeek) : 0
  const verdict = gapDays <= HIRE_WATCH ? 'fine' : gapDays <= HIRE_SIGNAL ? 'watch' : 'hire'
  return { gapDays, committedDays, overCommitted, hires, daysPerWeek, addedPerWeek, weeksToClear, verdict }
}

/* ── Flow forecasting + aging (established PM concepts) ──────────────────────────────────────────
 * Little's Law: time-to-clear = WIP ÷ throughput. Aging WIP: how long in-progress work has been open
 * (drift catches what due-dates miss). Both computable from data we already have. */

/** Little's Law: weeks to clear `wip` open items at `perWeek` completions/week. Null if no throughput. */
export function clearWeeks(wip, perWeek) {
  return perWeek > 0 ? Math.round((wip / perWeek) * 10) / 10 : null
}

/**
 * Aging WIP — open milestones that have started (Earliest Start in the past) but aren't posted, ranked
 * by age in days. Long age + not done = drift.
 * @param {Array} milestones raw open-milestone rows (getAgingMilestones)
 */
export function rollupAging(milestones, todayYmd) {
  return (milestones || []).map(m => {
    const f = m.fields || {}
    const est = fOfv(f['Earliest Start (EST)'])
    const age = est ? daysAgo(String(est).slice(0, 10), todayYmd) : null
    return {
      id: m.id,
      name: cleanStr(f['Full Deliverable Name'] || f['Name']) || '—',
      project: cleanStr(f['Project Name']),
      age,
      due: fOfv(f['Latest Finish (LFT)']) || null,
      // No owner field: RASCI is retired (docs/FLOW_RECONCILIATION_PLAN.md §0) and a queued
      // milestone has no owner by design. The view labels a row's crew from outline/crew.js.
      projectId: fOfv(f['Project']),
    }
  }).filter(r => r.age != null && r.age > 0).sort((a, b) => b.age - a.age)
}

/* ── Fever chart (CCPM portfolio health) ─────────────────────────────────────────────────────────
 * One dot per active project: x = % complete, y = working-days behind ("buffer consumed"). The firm
 * has no contracted end, so we use the scheduler's `pressure` (sum of negative slack) as the behind
 * measure. Danger = a lot behind while little is complete. */

export function rollupFever(phases, projectPressure) {
  const byProj = new Map()
  ;(phases || []).forEach(p => {
    const f = p.fields || {}
    const name = cleanStr(f['Project Name']); if (!name) return
    let pc = Number(fOfv(f['Percent Complete'])); if (!Number.isFinite(pc)) pc = 0; if (pc > 1.5) pc /= 100
    const w = Number(fOfv(f['Hours Budget'])) || 1
    const e = byProj.get(name) || { wsum: 0, wpc: 0 }
    e.wsum += w; e.wpc += pc * w; byProj.set(name, e)
  })
  const press = new Map((projectPressure || []).map(p => [p.name, p]))
  const zoneOf = behind => behind > 10 ? 'red' : behind > 3 ? 'amber' : 'green'
  const rows = []
  byProj.forEach((e, name) => {
    const pctComplete = e.wsum ? e.wpc / e.wsum : 0
    const pr = press.get(name)
    const behind = pr ? Number(pr.value) || 0 : 0
    rows.push({ name, pctComplete: Math.round(pctComplete * 100) / 100, behind: Math.round(behind * 10) / 10, overdue: pr ? (pr.overdue || 0) : 0, zone: zoneOf(behind) })
  })
  ;(projectPressure || []).forEach(p => {
    if (!byProj.has(p.name) && ((Number(p.value) || 0) > 0 || (p.overdue || 0) > 0)) {
      rows.push({ name: p.name, pctComplete: 0, behind: Math.round((Number(p.value) || 0) * 10) / 10, overdue: p.overdue || 0, zone: zoneOf(Number(p.value) || 0) })
    }
  })
  return rows.sort((a, b) => b.behind - a.behind)
}

/* ── Cycle time (backfillable: Earliest Start → Posted Date) ─────────────────────────────────────
 * How long milestones actually take, start to finish. Both dates already exist on every milestone, so
 * this is computable across all past work today (unlike flow efficiency, whose wait-time must accrue). */
export function rollupCycleTime(milestones) {
  const items = (milestones || []).map(m => {
    const f = m.fields || {}
    const est = fOfv(f['Earliest Start (EST)']), posted = fOfv(f['Posted Date'])
    const a = est ? Date.parse(String(est).slice(0, 10) + 'T00:00:00Z') : NaN
    const b = posted ? Date.parse(String(posted).slice(0, 10) + 'T00:00:00Z') : NaN
    const days = (Number.isNaN(a) || Number.isNaN(b)) ? null : Math.round((b - a) / 86400000)
    return { name: cleanStr(f['Full Deliverable Name'] || f['Name']) || '—', project: cleanStr(f['Project Name']), days }
  }).filter(r => r.days != null && r.days >= 0)
  const arr = items.map(r => r.days).sort((a, b) => a - b)
  const pctile = p => arr.length ? arr[Math.min(arr.length - 1, Math.floor(p * arr.length))] : null
  return { count: items.length, median: pctile(0.5), p90: pctile(0.9), slowest: items.slice().sort((a, b) => b.days - a.days).slice(0, 8) }
}

/* ── Cost of Delay → CD3 (economic priority) ─────────────────────────────────────────────────────
 * Relative Cost of Delay derived from signals we already have (project priority + time-criticality +
 * urgency/role), divided by a rough duration → CD3, so "what's worth doing next" weighs value per
 * effort. Relative proxies, never dollars. */

const PRIORITY_VALUE = { High: 3, Critical: 4, Medium: 2, Normal: 2, Low: 1 }

export function costOfDelay(n, todayYmd) {
  const value = PRIORITY_VALUE[n.priority] || 2
  const b = bucketLite(n.due, todayYmd)
  const timeCrit = b === 'overdue' ? 3 : b === 'soon' ? 2 : b === 'later' ? 1 : 0.5
  const u = String(n.urgency || '').toUpperCase()
  const risk = u.includes('IMPOSSIBLE') ? 3 : u.includes('URGENT') ? 2 : (n.role === 'Responsible' ? 1 : 0)
  return value + timeCrit + risk
}
export function cd3(n, todayYmd) {
  const dur = Math.max(0.25, Number(n.effortDays) || 0.5)
  return Math.round((costOfDelay(n, todayYmd) / dur) * 10) / 10
}
function bucketLite(due, todayYmd) {
  if (!due) return 'nodate'
  const d = Date.parse(String(due).slice(0, 10) + 'T00:00:00Z'), t = Date.parse(todayYmd + 'T00:00:00Z')
  if (Number.isNaN(d) || Number.isNaN(t)) return 'nodate'
  if (d < t) return 'overdue'
  if (d <= t + 7 * 86400000) return 'soon'
  return 'later'
}

/* ── Today cockpit (CT-025 Phase A) ──────────────────────────────────────────────────────────────
 * The daily front door: a "Flow" health index (seed of the composite progress score) and the unified
 * "what needs you" exception feed — over-committed people, unowned work, stalls, over-budget — ranked
 * so a manager sees the few things that need a human in one place. */

/** Flow = how smoothly work is moving (0–100, higher = smoother). Seed of the composite score. */
export function flowScore(summary) {
  if (!summary) return null
  let s = 100
  s -= (summary.overloaded || 0) * 8
  s -= (summary.tight || 0) * 3
  s -= (summary.stalled || 0) * 4
  s -= (summary.overdue || 0) * 3
  s -= (summary.atRisk || 0) * 1.5
  return Math.max(0, Math.min(100, Math.round(s)))
}

/**
 * Unified, ranked "needs attention" feed from the team model + efficiency rollup.
 * @returns Array<{ id, sev, kind, label, sub, action }> (sev 3 high → 1 low)
 */
export function buildTodayFeed(model, eff) {
  const items = []
  ;((model && model.people) || []).forEach(p => {
    const m = p.metrics
    if (m.status === 'overloaded' || m.status === 'tight') {
      items.push({ id: 'cap:' + p.memberId, sev: m.status === 'overloaded' ? 3 : 2, kind: 'capacity',
        label: `${p.name} is ${m.status === 'overloaded' ? 'over capacity' : 'tight'}`, sub: (m.why && m.why[0]) || '',
        action: { type: 'rebalance', memberId: p.memberId } })
    } else if (m.stalled) {
      items.push({ id: 'stp:' + p.memberId, sev: 2, kind: 'stalled', label: `${p.name} looks stalled`, sub: (m.why.find(w => /stalled/i.test(w))) || 'no recent activity', action: { type: 'rebalance', memberId: p.memberId } })
    }
  })
  ;((eff && eff.unassigned) || []).forEach(u => {
    items.push({ id: 'un:' + u.id, sev: 3, kind: 'unassigned', label: `No owner — ${u.name}`,
      sub: [u.project, u.due ? 'due ' + u.due : null].filter(Boolean).join(' · '),
      action: { type: 'assign', msId: u.id, projectId: u.projectId, label: u.name } })
  })
  ;((eff && eff.stalled) || []).forEach(s => {
    items.push({ id: 'st:' + s.project + s.phase, sev: 1, kind: 'stalled', label: `Stalled — ${s.project} · ${s.phase}`,
      sub: `${s.openMs} open · idle ${s.idle == null ? 'never logged' : s.idle + 'd'}`, action: { type: 'efficiency' } })
  })
  ;((eff && eff.overBudget) || []).slice(0, 5).forEach(o => {
    items.push({ id: 'ob:' + o.project + o.phase, sev: 1, kind: 'overbudget', label: `Over budget — ${o.project} · ${o.phase}`,
      sub: `${Math.round(o.pctUsed * 100)}% of hours used vs ${Math.round(o.pctComplete * 100)}% complete`, action: { type: 'efficiency' } })
  })
  return items.sort((a, b) => b.sev - a.sev).slice(0, 14)
}

/* ── Exception engine (CT-025 R2 — the cockpit's heart) ────────────────────────────────────────
 * Turn the team model + efficiency rollup into ONE severity-ranked set of exceptions the
 * autoscheduler can't resolve on its own. Each carries a blast radius, a tier
 * (auto / onetap / escalate), and a SPECIFIC recommended move (reassign to a named person, assign,
 * defer, mark reactive, review). Pure + testable. The UI executes the move via
 * services/teamOverview; the nightly scheduler re-levels around it. Auto-APPLY itself lands with the
 * scheduler slice — here we only CLASSIFY the tier so the cockpit can collapse the safe majority and
 * surface a vital few. Root-cause (predecessor-cascade) grouping needs the scheduler's predecessor
 * graph and lands with the decision ledger; for now same-kind items batch via `groupKey`. */

const EXC_SEV = { overloaded: 3, unowned: 3, tight: 2, stalled: 2, doneNotClosed: 1, overbudget: 1 }

/** The lightest under-capacity, on-roster person other than `excludeMemberId` — the reassign target. */
export function lightestTarget(people, excludeMemberId) {
  return (people || [])
    .filter(p => p.onRoster && p.metrics && p.metrics.status === 'light' && p.memberId !== excludeMemberId)
    .sort((a, b) => (a.metrics.loadRatio ?? 0.5) - (b.metrics.loadRatio ?? 0.5))[0] || null
}

export function buildExceptions(model, eff, todayYmd, opts = {}) {
  const people = (model && model.people) || []
  // Eligibility context: role-defined skills (heavy) + demonstrated (learned) + manual overrides.
  const elig = { skillsByMember: opts.skillsByMember, roleSkills: opts.roleSkills, demonstratedByMember: opts.demonstratedByMember }
  const out = []

  // 1) Over-committed / tight people → reassign their most-pressing milestone to the best ELIGIBLE
  //    candidate (right skill + availability), not just whoever is least busy.
  people.forEach(p => {
    const m = p.metrics || {}
    if (m.status !== 'overloaded' && m.status !== 'tight') return
    const item = (m.doing || []).find(n => n.type === 'Deliverable') || null
    const cand = item ? bestCandidate(item.label, people, elig, { excludeMemberId: p.memberId }) : null
    out.push({
      id: 'cap:' + p.memberId, kind: 'capacity', severity: EXC_SEV[m.status],
      blast: m.impossible ? Math.max(1, Math.round(m.impossible)) : (m.overdue || 1),
      tier: cand && item ? 'onetap' : 'escalate',
      title: `${p.name} is ${m.status === 'overloaded' ? 'over capacity' : 'tight'}`,
      why: (m.why && m.why[0]) || '',
      entity: item ? { type: 'milestone', id: item.entityId, name: item.label, project: item.projectName, projectId: item.projectId }
                   : { type: 'person', id: p.memberId, name: p.name },
      recommended: cand && item
        ? { action: 'reassign', toMemberId: cand.memberId, toName: cand.name, msId: item.entityId, eligibility: cand.reason, label: `Reassign “${item.label}”${item.projectName ? ` · ${item.projectName}` : ''} to ${cand.name} — ${cand.reason}` }
        : { action: 'defer', label: 'No eligible person with spare capacity — defer lower-priority work, pull a deadline, or add capacity' },
    })
  })

  // 2) Unowned milestones → assign to the best ELIGIBLE candidate (batched by the UI), else escalate.
  ;((eff && eff.unassigned) || []).forEach(u => {
    const cand = bestCandidate(u.name, people, elig)
    out.push({
      id: 'un:' + u.id, kind: 'unowned', severity: EXC_SEV.unowned, blast: 1,
      tier: cand ? 'onetap' : 'escalate', groupKey: 'unowned',
      title: `No owner — ${u.name}`,
      why: [u.project, u.due ? 'due ' + u.due : null].filter(Boolean).join(' · '),
      entity: { type: 'milestone', id: u.id, name: u.name, project: u.project, projectId: u.projectId },
      recommended: cand
        ? { action: 'assign', toMemberId: cand.memberId, toName: cand.name, msId: u.id, eligibility: cand.reason, label: `Assign to ${cand.name} — ${cand.reason}` }
        : { action: 'assign', msId: u.id, label: 'Assign an owner — no one is clearly eligible and free' },
    })
  })

  // 3) Stalled people → judgment call (confirm not blocked) → escalate with a suggested move.
  people.forEach(p => {
    const m = p.metrics || {}
    if (!m.stalled || m.status === 'overloaded' || m.status === 'tight') return
    out.push({
      id: 'stall:' + p.memberId, kind: 'stalled', severity: EXC_SEV.stalled, blast: m.doingCount || 1,
      tier: 'escalate',
      title: `${p.name} looks stalled`,
      why: (m.why && m.why.find(w => /stalled/i.test(w))) || 'no recent activity',
      entity: { type: 'person', id: p.memberId, name: p.name },
      recommended: { action: 'review', label: 'Confirm it is not blocked on a dependency or the client, then unblock or reassign' },
    })
  })

  // 4) Over-budget phases → a scope/fee conversation, not a one-click.
  ;((eff && eff.overBudget) || []).slice(0, 6).forEach(o => {
    out.push({
      id: 'ob:' + o.project + o.phase, kind: 'overbudget', severity: EXC_SEV.overbudget, blast: 1,
      tier: 'escalate',
      title: `Over budget — ${o.project} · ${o.phase}`,
      why: `${Math.round((o.pctUsed || 0) * 100)}% of hours used vs ${Math.round((o.pctComplete || 0) * 100)}% complete`,
      entity: { type: 'phase', id: null, name: `${o.project} · ${o.phase}`, project: o.project },
      detail: { project: o.project, phase: o.phase, pctUsed: o.pctUsed, pctComplete: o.pctComplete, budget: o.budget, expended: o.expended },
      recommended: { action: 'review', label: 'Review scope / fee — burning faster than it is progressing' },
    })
  })

  out.sort((a, b) => b.severity - a.severity || b.blast - a.blast)
  return out
}

const BATCH_LABEL = { unowned: 'milestones have no owner' }

/**
 * Split exceptions into the cockpit's buckets: the vital-few one-tap decisions (capped + BATCHED),
 * the escalations needing a real call, and the auto-handled (collapsed). This is the "never 20
 * decisions a day" mechanism — same-cause items collapse into ONE decision (82 unowned milestones
 * become a single "82 milestones have no owner" row, not 82 buttons), and the human-facing set is
 * then capped. Batches expose their members for the surface to expand.
 */
export function triageExceptions(exceptions, opts = {}) {
  const cap = opts.decisionCap || 6
  const onetap = (exceptions || []).filter(e => e.tier === 'onetap')

  const groups = new Map()
  const singles = []
  onetap.forEach(e => {
    if (e.groupKey) { if (!groups.has(e.groupKey)) groups.set(e.groupKey, []); groups.get(e.groupKey).push(e) }
    else singles.push(e)
  })
  const batched = []
  groups.forEach((items, key) => {
    if (items.length === 1) { singles.push(items[0]); return }
    batched.push({
      id: 'batch:' + key, batch: true, groupKey: key, kind: items[0].kind,
      severity: Math.max(...items.map(i => i.severity || 0)), count: items.length, items,
      title: `${items.length} ${BATCH_LABEL[key] || key}`,
    })
  })

  const ordered = [...batched, ...singles].sort((a, b) => (b.severity || 0) - (a.severity || 0) || (b.count || 0) - (a.count || 0))
  return {
    decisions: ordered.slice(0, cap),
    moreDecisions: Math.max(0, ordered.length - cap),
    escalations: (exceptions || []).filter(e => e.tier === 'escalate'),
    autoHandled: (exceptions || []).filter(e => e.tier === 'auto'),
    total: (exceptions || []).length,
  }
}

/**
 * Compact the computed dashboard model into a small JSON context for the AI coaching layer
 * (api/practice-chat). Only the numbers a human would actually reason about — kept tight so the
 * prompt stays cheap and the model never invents figures it cannot see. Pure; everything is read
 * from rollups the dashboard already built.
 */
export function buildCoachContext({ firm, model, eff, fever } = {}) {
  const r = (firm && firm.rollup) || {}
  const cap = (firm && firm.capacity) || {}
  const sum = (model && model.summary) || {}
  const modelPeople = (model && model.people) || []
  const people = modelPeople.length
    ? modelPeople.map(p => ({
        name: p.name,
        role: p.role,
        load: p.metrics && p.metrics.load != null ? round(p.metrics.load, 2) : null,
        hours7d: p.metrics && p.metrics.hours7d != null ? round(p.metrics.hours7d, 1) : null,
        status: p.metrics && p.metrics.status,
        activeNow: p.metrics && p.metrics.activeNow,
        overdue: p.metrics && p.metrics.overdue,
        why: (p.metrics && p.metrics.why) || [],
      }))
    // Fallback: the firm rollup already carries per-person load + 7d hours (no model needed on the
    // Performance tab). Throughput is joined by name where present.
    : (r.loadByPerson || []).map(p => {
        const thru = (r.throughputByPerson || []).find(t => t.name === p.name)
        return { name: p.name, load: p.value != null ? round(p.value, 2) : null, hours7d: p.hours7d, completed7d: thru ? thru.value : undefined }
      })
  const projects = (fever && fever.length)
    ? fever.map(f => ({ name: f.name, pctComplete: round(f.pctComplete || 0, 2), daysBehind: round(f.behind || 0, 0), overdue: f.overdue || 0, zone: f.zone }))
        .sort((a, b) => b.daysBehind - a.daysBehind).slice(0, 12)
    // Fallback to the rollup's project pressure (working-days behind + overdue count) when the fever
    // model (which needs phase data) has not been loaded.
    : (r.projectPressure || []).slice(0, 12).map(p => ({ name: p.name, daysBehind: round(p.value || 0, 0), overdue: p.overdue || 0 }))
  return {
    asOf: (firm && firm.snapshotDate) || null,
    daysOfHistory: (firm && firm.days) || 0,
    firm: {
      people: r.people, activePeople: r.activePeople,
      avgLoad: r.avgLoad, overloaded: r.overloaded, light: r.light,
      throughput7d: r.throughput7d, throughput28d: r.throughput28d,
      atRisk: r.atRisk, overdue: r.overdue,
      committedDays: cap.committedDays, infeasibleDays: cap.infeasibleDays,
      overCapacityPeople: cap.over, tightPeople: cap.tight,
    },
    people,
    projects,
    efficiency: eff ? { overBudget: (eff.overBudget || []).length, stalled: (eff.stalled || []).length, unowned: (eff.unassigned || []).length } : null,
    needsAttention: sum.needsAttention != null ? sum.needsAttention : undefined,
  }
}

/* ── Overnight report (trust layer — observability) ───────────────────────────────────────────────
 * Make the nightly scheduler's work legible: what it moved and WHY, from the run payload we already
 * persist (schedule_runs.payload). Pure; the reason per milestone is derived from the run fields. */

/** A plain-language reason a milestone moved, derived from its run-payload fields. */
function moveReason(m) {
  if (m.date_pinned) return 'Held — pinned'
  if (m.status === 'DEFERRED') return m.contact === 'Unassigned' ? 'Deferred — no owner to schedule against' : `Deferred — ${m.contact} at capacity`
  if (m.is_pushed && (m.push_count || 0) >= 8) return `Chronic slippage — pushed ${m.push_count}× with no completion`
  if (m.is_pushed) return 'Pushed out — to fit capacity or an upstream item'
  if (m.is_pulled) return 'Pulled in — capacity freed up'
  return 'Scheduled'
}

/**
 * Summarize a nightly run for the Overnight report: firm stats + the milestones that actually moved,
 * each with a derived reason, chronic-slippage flagged. Input is schedule_runs.payload (briefing_data).
 */
export function summarizeRun(payload) {
  if (!payload) return null
  const s = payload.stats || {}
  const ms = Array.isArray(payload.milestones) ? payload.milestones : []
  const movers = ms
    .filter(m => m.is_pushed || m.is_pulled || m.status === 'DEFERRED' || m.date_pinned)
    .map(m => ({
      name: m.milestone, project: m.project, who: m.contact, status: m.status,
      pushed: !!m.is_pushed, pulled: !!m.is_pulled, pushCount: m.push_count || 0,
      reason: moveReason(m),
    }))
    .sort((a, b) => (b.pushCount - a.pushCount))
  return {
    date: payload.date || null,
    stats: {
      scheduled: n(s.milestones_scheduled), pushed: n(s.pushed_later), pulled: n(s.pulled_earlier),
      cascade: n(s.cascade_updates), blocked: n(s.blocked_by_predecessor), skippedFuture: n(s.skipped_future), pinned: n(s.pinned),
    },
    movers,
    chronic: movers.filter(m => m.pushCount >= 8),
  }
}

/* ── Firm Health over time (the "are we improving?" score — the cardio analog) ─────────────────────
 * A composite 0–100 firm-health index, bucketed WEEKLY from the snapshot history, with its direction
 * called out in plain language. Components (each also trended): schedule risk, load balance, schedule
 * stability (churn), throughput. Relative/bounded so the score has headroom and movement is meaningful;
 * the VALUE is the week-over-week direction, not an absolute benchmark. Honestly guards thin history. */

const clamp100 = x => Math.max(0, Math.min(100, Math.round(x)))
function groupByDate(rows) { const m = {}; (rows || []).forEach(r => { const d = r.snapshot_date; if (d) (m[d] = m[d] || []).push(r) }); return m }
function weekKey(ymd) {
  const t = Date.parse(String(ymd) + 'T00:00:00Z')
  if (Number.isNaN(t)) return String(ymd)
  const dt = new Date(t)
  dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7))   // back up to Monday
  return dt.toISOString().slice(0, 10)
}

export function firmHealthScore(history = {}, lv = {}) {
  const hot = lv.load_hot ?? HOT
  const memByDate = groupByDate(history.members), projByDate = groupByDate(history.projects)
  // Each week → its LATEST snapshot date (point-in-time metrics), runs aggregated per week.
  const weekLatest = {}
  Object.keys(memByDate).sort().forEach(d => { weekLatest[weekKey(d)] = d })
  const runsByWeek = {}
  ;(history.runs || []).forEach(r => { const wk = weekKey(r.run_date); (runsByWeek[wk] = runsByWeek[wk] || []).push(r) })

  let weeks = Object.keys(weekLatest).sort().map(wk => {
    const mRows = memByDate[weekLatest[wk]] || []
    const pRows = projByDate[weekLatest[wk]] || []
    const wkRuns = runsByWeek[wk] || []
    // Load balance: tight around 1.0, few chronically hot.
    const loads = mRows.map(m => m.load_ratio).filter(v => v != null).map(Number)
    const loadBalance = loads.length ? clamp100(100 - avg(loads.map(v => Math.abs(v - 1))) * 60 - (loads.filter(v => v > hot).length / loads.length) * 40) : null
    // Schedule risk: share of active work that's overdue/at-risk (lower = healthier).
    const overdue = mRows.reduce((s, m) => s + n(m.overdue), 0)
    const atRisk = mRows.reduce((s, m) => s + n(m.at_risk), 0)
    const active = mRows.reduce((s, m) => s + n(m.active_responsible), 0)
    const scheduleRisk = clamp100(100 - ((overdue + 0.5 * atRisk) / Math.max(active, overdue + atRisk, 1)) * 120)
    return {
      week: wk, loadBalance, scheduleRisk,
      churnRaw: wkRuns.length ? avg(wkRuns.map(r => n(r.pushed) + n(r.pulled))) : null,
      throughputRaw: mRows.reduce((s, m) => s + n(m.completions_7d), 0),
    }
  })
  // Normalize churn (lower better) + throughput (higher better) across the span.
  const maxChurn = Math.max(1, ...weeks.map(w => w.churnRaw).filter(v => v != null))
  const maxThru = Math.max(1, ...weeks.map(w => w.throughputRaw))
  weeks.forEach(w => {
    w.stability = w.churnRaw == null ? null : clamp100(100 - (w.churnRaw / maxChurn) * 70)
    w.throughput = clamp100((w.throughputRaw / maxThru) * 100)
    const parts = [[w.scheduleRisk, 0.35], [w.loadBalance, 0.25], [w.stability, 0.20], [w.throughput, 0.20]].filter(p => p[0] != null)
    const wsum = parts.reduce((s, p) => s + p[1], 0)
    w.score = wsum ? Math.round(parts.reduce((s, p) => s + p[0] * p[1], 0) / wsum) : null
  })
  weeks = weeks.filter(w => w.score != null)

  const latest = weeks[weeks.length - 1] || null
  const prior = weeks.slice(Math.max(0, weeks.length - 5), weeks.length - 1)
  const priorAvg = prior.length ? avg(prior.map(w => w.score)) : null
  const delta = (latest && priorAvg != null) ? Math.round(latest.score - priorAvg) : null
  const direction = delta == null ? 'flat' : delta >= 3 ? 'improving' : delta <= -3 ? 'declining' : 'flat'
  return {
    score: latest ? latest.score : null,
    direction, delta, weeksOfData: weeks.length, sparse: weeks.length < 3,
    series: weeks.map(w => ({ x: w.week, y: w.score })),
    components: latest ? { scheduleRisk: latest.scheduleRisk, loadBalance: latest.loadBalance, stability: latest.stability, throughput: latest.throughput } : null,
    componentSeries: {
      scheduleRisk: weeks.map(w => ({ x: w.week, y: w.scheduleRisk })),
      loadBalance: weeks.map(w => ({ x: w.week, y: w.loadBalance })),
      stability: weeks.map(w => ({ x: w.week, y: w.stability })),
      throughput: weeks.map(w => ({ x: w.week, y: w.throughput })),
    },
  }
}

/**
 * Nightly churn over time — how many milestones the scheduler had to MOVE each night (pushed +
 * pulled). The headline stability signal: less churn = the plan is holding and the team can service
 * it. One point per day (the live run), oldest→newest. Lower is better, so "improving" = falling.
 */
export function nightlyChurn(runs) {
  const byDate = {}
  ;(runs || []).filter(r => r && (!r.mode || r.mode === 'live') && r.run_date).forEach(r => {
    if (!byDate[r.run_date]) byDate[r.run_date] = r   // history is newest-first → first seen is the latest run that day
  })
  const series = Object.values(byDate)
    .map(r => ({ x: r.run_date, y: n(r.pushed) + n(r.pulled) }))
    .sort((a, b) => (a.x < b.x ? -1 : a.x > b.x ? 1 : 0))
  if (!series.length) return null
  const latest = series[series.length - 1].y
  const prior = series.slice(Math.max(0, series.length - 6), series.length - 1)
  const avgPrior = prior.length ? Math.round(avg(prior.map(s => s.y))) : null
  const delta = avgPrior != null ? latest - avgPrior : null
  const direction = delta == null ? 'flat' : delta <= -3 ? 'improving' : delta >= 3 ? 'rising' : 'flat'
  return { series, latest, avgPrior, delta, direction }
}

/** Latest value of a [{x,y}] series with its change vs the previous point. */
export function scoreWithDelta(series) {
  const pts = (series || []).filter(p => p.y != null)
  if (!pts.length) return { value: null, prev: null, delta: null, dir: 'flat' }
  const value = pts[pts.length - 1].y
  const prev = pts.length > 1 ? pts[pts.length - 2].y : null
  const delta = prev == null ? null : round(value - prev, 2)
  const dir = delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down'
  return { value, prev, delta, dir }
}
